import { useEffect, useState } from 'react';
import { Get } from '../utils/RemoteAuthority';
import TopBar from './TopBar';
import { SetTitle } from '../utils/Lobby';
import CollapsedCard from './CollpasedCard';
import './Lobby.css';
const CardConverter = require('@tta/engine/Utilities/Converters/CardConverter');

const percent = (rate) => rate === null || rate === undefined ? '–' : `${Math.round(rate * 100)}%`;
const place = (value) => value === null || value === undefined ? '–' : value.toFixed(1);
const winRate = (side) => side ? percent(side.wins / side.games) : '–';

// Your stats page (the server's GET /games/stats): your record, what the numbers say about your choices, your best
// games, what you like to play (leaders, wonders, technologies, governments, what you build) and how you do against
// each of the others; and in a tab of its own, the events you prepared
function Stats()
{
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    // Which tab: the overview, or the events you prepared (a long list of its own)
    const [tab, setTab] = useState('overview');
    useEffect(() => {
        SetTitle('Your stats · Through the Ages');
        Get('/games/stats')
            .then(result => result.ok === false ? setError(result.error) : setStats(result))
            .catch(() => setError('The game server is not answering.'));
    }, []);

    const { record, bests } = stats ?? {};
    return <div className="tta-lobby">
        <TopBar page="stats"/>
        <main className="tta-lobby-main">
            <h1 className="tta-lobby-heading">Your stats</h1>
            {error && <div className="tta-lobby-error">{error}</div>}
            {!stats && !error && <div className="tta-muted">Counting…</div>}
            {stats && record.games === 0 && <div className="tta-muted">Your stats appear here once you've played a game.</div>}
            {stats && record.games > 0 && <div className="tta-segmented tta-stats-tabs">
                <button type="button" className={tab === 'overview' ? 'on' : ''} onClick={() => setTab('overview')}>Overview</button>
                <button type="button" className={tab === 'events' ? 'on' : ''} onClick={() => setTab('events')}>Events you prepared</button>
            </div>}
            {stats && record.games > 0 && tab === 'events' && <PreparedEvents events={stats.events}/>}
            {stats && record.games > 0 && tab === 'overview' && <>
                <div className="tta-tiles">
                    <Tile value={record.games} label="games"/>
                    <Tile value={record.finished} label="finished"/>
                    <Tile value={record.wins} label={`win${record.wins === 1 ? '' : 's'}`} accent/>
                    <Tile value={percent(record.winRate)} label="win rate"/>
                    <Tile value={place(record.averagePlace)} label="average place"/>
                    <Tile value={record.averageCulture ?? '–'} label="average culture"/>
                    <Tile value={record.bestCulture ?? '–'} label="best culture"/>
                </div>

                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">What the numbers say</h2>
                    {stats.tidbits.length === 0
                        ? <div className="tta-muted">Finish a few more games: once you've made a choice in at least {stats.minimumGames} finished
                            games and skipped it in {stats.minimumGames} others, this tells you how it tends to go.</div>
                        : <div className="tta-tidbits">
                            {stats.tidbits.map(tidbit =>
                                <div key={`${tidbit.kind}-${tidbit.name}`} className={`tta-panel tta-tidbit ${tidbit.good ? 'good' : 'bad'}`}>
                                    <span className="tta-tidbit-sign">{tidbit.good ? '▲' : '▼'}</span>{tidbit.text}
                                </div>)}
                        </div>}
                </section>

                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Your best games</h2>
                    {!bests.best && !bests.longest ? <div className="tta-muted">These appear once you've finished a game.</div> :
                        <div className="tta-record-grid">
                            {bests.best && <Record label="Highest score" value={`${bests.best.culture} culture`} detail={<><GameLink game={bests.best}/>{bests.best.place === 1 ? ', a win' : `, ${Ordinal(bests.best.place)} place`}</>}/>}
                            {bests.biggest && <Record label="Biggest win" value={`by ${bests.biggest.margin}`} detail={<>over {bests.biggest.runnerUp} in <GameLink game={bests.biggest}/></>}/>}
                            {bests.closest && <Record label="Closest finish" value={bests.closest.margin === 0 ? 'a tie' : `by ${bests.closest.margin}`}
                                detail={<>{bests.closest.won ? `ahead of ${bests.closest.other}` : `behind ${bests.closest.other}`} in <GameLink game={bests.closest}/></>}/>}
                            {bests.longest && <Record label="Longest game" value={`${bests.longest.rounds} rounds`} detail={<GameLink game={bests.longest}/>}/>}
                        </div>}
                </section>

                <ChoiceSection title="Leaders" note="How often you put each into play when it was in the game" rows={stats.leaders} rateLabel="Pick rate" usedLabel="Played"/>
                <ChoiceSection title="Wonders" note="How often you complete each when it is in the game" rows={stats.wonders} rateLabel="Build rate" usedLabel="Completed"/>
                <ChoiceSection title="Technologies" note="How often you develop each, by game" rows={stats.technologies} rateLabel="Rate" usedLabel="Developed"/>
                <ChoiceSection title="Governments" note="How often you take each, by game" rows={stats.governments} rateLabel="Rate" usedLabel="Taken"/>
                <ChoiceSection title="What you build" note="Games in which you built more of each than you started with, and the most you had at once"
                    rows={stats.built} rateLabel="Rate" usedLabel="Built" most/>

                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">You against the others</h2>
                    {stats.rivals.length === 0 ? <div className="tta-muted">Finish a game to see how you stack up.</div> :
                        <div className="tta-rivals">
                            {stats.rivals.map(rival => {
                                const total = rival.ahead + rival.behind || 1;
                                return <div key={rival.login} className="tta-panel tta-rival">
                                    <div className="tta-rival-head"><b>{rival.name}</b><span className="tta-muted">{rival.games} game{rival.games === 1 ? '' : 's'} together</span></div>
                                    <div className="tta-rival-bar" title={`Ahead of ${rival.name} ${rival.ahead} times, behind ${rival.behind}`}>
                                        <span className="ahead" style={{ width: `${rival.ahead / total * 100}%` }}/>
                                        <span className="behind" style={{ width: `${rival.behind / total * 100}%` }}/>
                                    </div>
                                    <div className="tta-rival-numbers"><span>You finished ahead {rival.ahead}×</span><span>behind {rival.behind}×</span></div>
                                </div>;
                            })}
                        </div>}
                </section>
            </>}
        </main>
    </div>;
}

// What you play of one kind, most used first: how often, and how your finished games went with it and without it
const Shown = 10;
function ChoiceSection({title, note, rows, rateLabel, usedLabel, most = false})
{
    const [all, setAll] = useState(false);
    if (!rows.length)
    {
        return null;
    }
    const shown = all ? rows : rows.slice(0, Shown);
    return <section className="tta-lobby-section">
        <h2 className="tta-lobby-section-title">{title} <span className="tta-muted">{note}</span></h2>
        <div className="tta-panel tta-table-wrap">
            <table className="tta-stats-table">
                <thead><tr>
                    <th>{title === 'What you build' ? 'Kind' : title.replace(/s$/, '').replace(/ie$/, 'y')}</th>
                    <th>{usedLabel}</th><th>{rateLabel}</th>
                    {most && <th>Most at once</th>}
                    <th title="In finished games: how often you won with it, and without it">Win rate with / without</th>
                    <th title="In finished games: your average place with it, and without it">Avg. place with / without</th>
                </tr></thead>
                <tbody>
                    {shown.map(row =>
                        <tr key={row.name}>
                            <td>{row.name}</td>
                            <td>{row.tally.games} of {row.tally.of}</td>
                            <td>{percent(row.tally.rate)}</td>
                            {most && <td>{row.most}</td>}
                            <td>{winRate(row.tally.with)} / {winRate(row.tally.without)}</td>
                            <td>{place(row.tally.with?.place)} / {place(row.tally.without?.place)}</td>
                        </tr>)}
                </tbody>
            </table>
        </div>
        {rows.length > Shown && <button type="button" className="tta-link-button tta-show-all" onClick={() => setAll(!all)}>
            {all ? 'Show fewer' : `Show all ${rows.length}`}</button>}
    </section>;
}

// The events you prepared in your finished games, by age, the most prepared first: each card (hover for it), how many
// times, and in how many games; Confucius's non-events only as a count
const EventAges = ['Age A', 'Age I', 'Age II', 'Age III'];
function PreparedEvents({events})
{
    if (events.games === 0)
    {
        return <div className="tta-muted">These appear once you've finished a game: who prepared which events stays secret until then.</div>;
    }
    return <section className="tta-lobby-section">
        <div className="tta-muted tta-field-note">
            You prepared {events.prepared} event{events.prepared === 1 ? '' : 's'} in your {events.games} finished game{events.games === 1 ? '' : 's'}.
        </div>
        {EventAges.map(age => {
            const cards = events.cards.filter(card => card.age === age);
            return cards.length > 0 && <div key={age} className="tta-prepared-age">
                <h2 className="tta-lobby-section-title">{age}</h2>
                <div className="tta-panel tta-prepared-stats">
                    {cards.map(card =>
                        <div key={`${card.type}-${card.name}`} className="tta-prepared-stat">
                            <CollapsedCard card={CardConverter({ type: card.type, name: card.name, age: card.age, id: -1 })}/>
                            <span className="tta-prepared-times">×{card.times}</span>
                            <span className="tta-muted">in {card.games} of {events.games} game{events.games === 1 ? '' : 's'}</span>
                        </div>)}
                </div>
            </div>;
        })}
        {events.nonEvents > 0 && <div className="tta-muted tta-field-note">
            Also {events.nonEvents} non-event{events.nonEvents === 1 ? '' : 's'} prepared with Confucius (other military cards, which aren't listed).
        </div>}
    </section>;
}

const Ordinal = (n) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`;

const Tile = ({ value, label, accent }) =>
    <div className={`tta-tile${accent ? ' accent' : ''}`}><div className="tta-tile-value">{value}</div><div className="tta-tile-label">{label}</div></div>;

const Record = ({ label, value, detail }) =>
    <div className="tta-panel tta-record"><div className="tta-record-label">{label}</div><div className="tta-record-value">{value}</div><div className="tta-muted">{detail}</div></div>;

// (one of our own games: it opens)
const GameLink = ({ game }) => <a href={`./?game=${game.id}`}><i>{game.title}</i></a>;

export default Stats;
